/** Ported from chief's `web/src/main.tsx` (MIT) — see "Licence and attribution" in README.md. */

import { type ComponentType, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import { AppShell } from './AppShell.tsx';
import { AppDataProvider } from './data.tsx';
import './index.css';
import { AdDetail } from './pages/AdDetail.tsx';
import { Ads } from './pages/Ads.tsx';
import { Board } from './pages/Board.tsx';
import { BrandBook } from './pages/BrandBook.tsx';
import { BrandBooks } from './pages/BrandBooks.tsx';
import { Content } from './pages/Content.tsx';
import { Contents } from './pages/Contents.tsx';
import { IdeaBatch } from './pages/IdeaBatch.tsx';
import { IdeaReview } from './pages/IdeaReview.tsx';
import { Ideas } from './pages/Ideas.tsx';
import { Library } from './pages/Library.tsx';
import { Login } from './pages/Login.tsx';
import { NewContent } from './pages/NewContent.tsx';
import { NewIdeaBatch } from './pages/NewIdeaBatch.tsx';
import { Overview } from './pages/Overview.tsx';
import { placeholder } from './pages/Placeholder.tsx';
import { Repositories } from './pages/Repositories.tsx';
import { Settings } from './pages/Settings.tsx';
import {
  adMetaIdFromPath,
  brandBookIdFromPath,
  contentIdFromPath,
  ideaBatchIdFromPath,
  useLocation,
} from './router.tsx';
import { ToastProvider } from './toast.tsx';
import { Skeleton } from './ui.tsx';

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

// The one page still waiting for its own story. The <Suspense> boundary
// further down is already here for the ones that will be `lazy()`: xterm.js is
// by far the largest dependency and only the pages with a terminal need it, so
// loading those separately keeps the first paint small.
const Terminals = placeholder({
  title: 'Terminals',
  icon: 'terminal',
  story: 'US-073 opens a shell in any running container here.',
  summary: 'A browser terminal attached to a content container.',
});

/**
 * The entry points, matched on the pathname. The server redirects
 * unauthenticated navigations to `/login` and serves index.html for the rest,
 * so every path here survives a reload.
 */
const PAGES: Record<string, ComponentType> = {
  '/': Overview,
  '/ads': Ads,
  '/board': Board,
  '/brand-books': BrandBooks,
  '/content': Contents,
  '/content/new': NewContent,
  '/ideas': Ideas,
  '/ideas/new': NewIdeaBatch,
  '/library': Library,
  '/repositories': Repositories,
  '/settings': Settings,
  '/terminal': Terminals,
  '/terminals': Terminals,
};

function resolve(pathname: string): ComponentType {
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const page = PAGES[trimmed];
  if (page !== undefined) return page;
  if (contentIdFromPath(trimmed) !== null) return Content;
  if (ideaBatchIdFromPath(trimmed) !== null) {
    return trimmed.endsWith('/review') ? IdeaReview : IdeaBatch;
  }
  if (brandBookIdFromPath(trimmed) !== null) return BrandBook;
  if (adMetaIdFromPath(trimmed) !== null) return AdDetail;
  // An unknown URL lands on the overview rather than on a blank screen.
  return Overview;
}

function App() {
  const { pathname } = useLocation();
  if (pathname === '/login') return <Login />;
  const Page = resolve(pathname);
  return (
    <AppDataProvider>
      <AppShell>
        <Suspense
          fallback={
            <div className="page">
              <div className="panel">
                <div className="panel__body">
                  <Skeleton lines={5} />
                </div>
              </div>
            </div>
          }
        >
          <Page />
        </Suspense>
      </AppShell>
    </AppDataProvider>
  );
}

createRoot(container).render(
  <StrictMode>
    <ToastProvider>
      <App />
    </ToastProvider>
  </StrictMode>,
);
